import { useEffect, useState } from 'react';
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip, Icon, Box, makeStyles, Button, Grid, IconButton } from "@material-ui/core";
import LinkIcon from '@material-ui/icons/Link';
import TanStackTable from '../../atoms/table/TanStackTable';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from '@material-ui/icons/Add';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import AddCommentOutlinedIcon from '@material-ui/icons/AddCommentOutlined';
import FieldForm from './FieldForm.js';
import { getViews } from '../../../store/views/selectors.js';
import { changeUuidsForObj, findSelectedSchema } from '../../../services/builder/util.js';
import { typeItems, urlItems } from '../../../config/fields.js';
import { updateViewSchema } from '../../../utils/DashboardBuilder.js';
import { handleSnackBar, invokePostService, setDashboardRefresh, setReRender, setViewElementToCopy, toggleRightDrawer } from '../../../store/global/actions.js';
import { editView } from '../../../store/views/actions.js';
import { getDetailsFromUrl } from '../../../services/blueprint.js';
import { getRightDrawerStatus, getViewElementToCopy } from '../../../store/global/selectors.js';
import DropdownOptions from '../../atoms/FeildExtensionDropdown/index.js';
import HyperlinkConfiguration from './HyperlinkConfiguration.js';


const useStyles = makeStyles((theme) => ({
    iconContainer: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center"
    },
    iconBox: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: theme.spacing(1.5),
        margin: 5
    },
    iconTooltip: {
        cursor: "pointer",
    },
    icon: {
        fontSize: 16
    },
    deleteIcon: {
        color: theme.palette.error.main,
        fontSize: 20
    },
}));


export default function Fields({ selectedWidget }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();

    const selectedView = useSelector(getViews);
    const rightDrawerStatus = useSelector(getRightDrawerStatus);
    const copiedField = useSelector(getViewElementToCopy);


    const [action, setAction] = useState(null);
    const [data, setData] = useState([]);
    const [selectedField, setSelectedField] = useState(null);
    const [parentEntity, setParentEntity] = useState(null);
    const [showHyperlinkConfiguration, setShowHyperlinkConfiguration] = useState(false);
    const [popupAnchorEl, setPopupAnchorEl] = useState(null);
    const [anchorPosition, setAnchorPosition] = useState({ top: 0, left: 0 });

    

    const { blueprintId, entityId, slug } = getDetailsFromUrl();

    const generateTableData = (entries, parentId) => {
        try {
            
        return entries
            .filter(item => item?.type !== "drop-down")
            .map(item => {
                if (item.children && item.children?.length > 0) {
                    item.children = generateTableData(item.children, item.id)
                }
                return {
                    ...item,
                    subRows: item.children && item.children.length > 0 ? item.children : undefined,
                    parentId: parentId
                };
            });


        } catch (error) {
            console.error(`Generating fields for the table failed. ${error}`);
            return [];
        }
    }

    const getHyperlinkStatus = (field) => {
        try {
            if(selectedWidget?.type === "table"){
                return (field?.viewname !== undefined || field?.clusterlettype !== undefined) ? "Enabled" : "Disabled" 
            }else{
                return "";
            }

        } catch (error) {
            console.error(`Finding hyperlink status failed. ${error}`);
            return "";
        }
    }

    const columns = [
        {
            accessorKey: "name",
            header: "Name",
            cell: ({ row, getValue }) => (
                <div style={{ display: 'flex', alignItems: 'center', paddingLeft: `${row.depth * 2}rem` }}>
                    {row.getCanExpand() ? (
                        <>
                            {row.getIsExpanded()
                                ? <ArrowDropDownIcon id="collapse_row" onClick={row.getToggleExpandedHandler()} style={{ cursor: "pointer" }} />
                                : <ArrowRightIcon id="expand_row" onClick={row.getToggleExpandedHandler()} style={{ cursor: "pointer" }} />
                            }
                        </>
                    ) : (
                        ""
                    )}
                    {getValue()}
                </div>
            ),
        },
        {
            accessorKey: "type",
            header: "Type",
            cell: ({ row, getValue }) => {
                const type = [...typeItems, ...urlItems].find(item => item.value === row.original.type);
                return type?.title || row?.original?.type;
            }
        },
        {
            accessorKey: "desc",
            header: "Description",
            cell: ({ row, getValue }) => (
                row.original.desc
            )
        },
        {
            accessorKey: "default",
            header: "Default",
            cell: ({ row }) => {
                const rawValue = row.original.default;
                const value = rawValue === undefined || rawValue === null
                    ? ""
                    : typeof rawValue === "object"
                        ? JSON.stringify(rawValue)
                        : String(rawValue);

                const displayText = value.length > 30 ? `${value.slice(0, 30)}...` : value;
                return value.length > 30 ? (
                    <Tooltip title={value} arrow>
                        <span>{displayText}</span>
                    </Tooltip>
                ) : (
                    <span>{displayText}</span>
                );
            }
        },
        {
            accessorKey: "unit",
            header: "Unit",
            cell: ({ row, getValue }) => (
                row.original.unit
            )
        },
        {
            accessorKey: "hyperlink",
            header: selectedWidget?.type === "table" ?  "Hyperlink": "",
            cell: ({ row, getValue }) => (
                getHyperlinkStatus(row.original)
            )
        },
        {
            accessorKey: "action",
            header: () => (
                <>
                    {copiedField &&
                        <Box display="flex" justifyContent="center" alignItems="center" gap={1} width="100%">
                            <Tooltip title="Paste field" className={classes.iconTooltip}>
                                <Icon id="field_paste" onClick={() => onPasteField(selectedWidget)}>
                                    <AddCommentOutlinedIcon className={classes.icon} color="white" />
                                </Icon>
                            </Tooltip>
                        </Box>
                    }
                </>
            ),
            cell: ({ row, getValue }) => (
                <Box className={classes.iconBox}>
                    {(row?.original?.type === "struct" || row?.original?.type === "table") &&
                        <>
                            <Tooltip title="Add field" className={classes.iconTooltip}>
                                <IconButton size="small" onClick={() => onCreateNestedField(row.original)}>
                                    <AddIcon className={classes.icon} color="primary" />
                                </IconButton>
                            </Tooltip>

                            {copiedField &&
                                <Tooltip title="Paste field" className={classes.iconTooltip}>
                                    <IconButton size="small" id="field_paste" onClick={() => onPasteField(row.original)}>
                                        <AddCommentOutlinedIcon className={classes.icon} color="primary" />
                                    </IconButton>
                                </Tooltip>
                            }
                        </>
                    }
                    {row.original?.type !== "drop-down" &&
                        <>
                            <Tooltip title="Edit field" className={classes.iconTooltip}>
                                <IconButton size="small" id="field_edit" onClick={() => onEditField(row.original)}>
                                    <EditIcon className={classes.icon} color="primary" />
                                </IconButton>
                            </Tooltip>
                            {   selectedWidget?.type === "table" &&
                                <Tooltip title="Configure Hyperlink" className={classes.iconTooltip}>
                                    <IconButton size="small" id="configure_hyperlink" onClick={(e) => onConfigureHyperlink(e, row.original) }>
                                        <LinkIcon className={classes.icon} color="primary" />
                                    </IconButton>
                                </Tooltip>
                            }
                            <Tooltip title="Copy field" className={classes.iconTooltip}>
                                <IconButton size="small" id="field_copy"  onClick={() => onCopyField(row.original)}>
                                    <FileCopyIcon className={classes.icon} color="primary" />
                                </IconButton>
                            </Tooltip>
                            {row.original.viewCopy !== true &&
                            <Tooltip title="Delete field" className={classes.iconTooltip}>
                                <IconButton size="small" id="field_delete"  onClick={() => onDeleteField(row.original)}>
                                    <DeleteIcon className={classes.deleteIcon} />
                                </IconButton>
                            </Tooltip>
                            } 
                        </>
                    }
                </Box>
            ),
        },
    ]

    useEffect(() => {
        setData(generateTableData(selectedWidget?.children ?? [], selectedWidget?.id));
    }, [selectedWidget]);

    const onCreateField = () => {
        setParentEntity(selectedWidget);
        setAction("create_field");
    }

    const onCreateNestedField = (field) => {
        setAction("create_field");
        setParentEntity(field);
    }

    const onEditField = (field) => {
        const parent = findSelectedSchema({ schemas: selectedView?.view_schema, id: field.parentId });
        setParentEntity(parent);
        setSelectedField(field);
        setAction("edit_field");
    }

    const onConfigureHyperlink = (event, field) => {
        try {
            setAnchorPosition({
                top: event.clientY,
                left: event.clientX
            });
            setPopupAnchorEl(event.currentTarget);
            setShowHyperlinkConfiguration(true);

            const parent = findSelectedSchema({ schemas: selectedView?.view_schema, id: field.parentId });
            setParentEntity(parent);
            setSelectedField(field);
        } catch (error) {
            console.error(`Opening hypelink configuration failed. ${error}`);
            setAnchorPosition({top: 0,left: 0});
        }
    }

    const onCopyField = (field) => {
        delete field.subRows;
        dispatch(setViewElementToCopy(field));
    }

    const onPasteField = (parentField) => {
        if (!copiedField || !parentField) return;
        const newField = changeUuidsForObj(copiedField)
        newField.name = getCopiedFieldName(parentField.children, newField.name);
        const updatedView = updateViewSchema(selectedView, 'add', { parentId: parentField.id, newItem: newField });
        const updatedWidget = findSelectedSchema({ schemas: updatedView.view_schema, id: selectedWidget.id });
        dispatch(setDashboardRefresh({ type: "widget", action: "edit", entity: updatedWidget }));
        handleViewUpdate(updatedView, "paste");
        dispatch(setViewElementToCopy(null))
    }

    const onDeleteField = (field) => {
        const updatedView = updateViewSchema(selectedView, 'delete', { id: field.id });
        const updatedWidget = findSelectedSchema({ schemas: updatedView.view_schema, id: selectedWidget.id });
        dispatch(setDashboardRefresh({ type: "widget", action: "edit", entity: updatedWidget }));
        handleViewUpdate(updatedView, "delete");
    }

    const getCopiedFieldName = (entities, copiedEntityName, index = 0) => {
        const newEntityName = `${copiedEntityName}_copy${(index && index !== 0) ? index : ''}`;
        for (let i = 0; i < entities?.length; i++) {
            if (entities[i]?.name === newEntityName) {
                return getCopiedFieldName(entities, copiedEntityName, index + 1)
            }
        }
        return newEntityName
    }

    const handleViewUpdate = (updatedView, action=null) => {
        return dispatch(invokePostService('updateView', { view: updatedView }, [entityId])).then(response => {
            if (response.status === true) {
                if(action){
                    dispatch(handleSnackBar({ open: true, message: `Field ${action === "delete" ? "deleted": "pasted"} `, severity: 'success' }));
                }
                dispatch(editView(updatedView));
            } else {
                if (response.code === 255) {
                    history.push(`/blueprint/${slug}/${blueprintId}/system-model`);
                    dispatch(setReRender());
                    if (rightDrawerStatus === true) dispatch(toggleRightDrawer());
                } else {
                    dispatch(editView(selectedView));
                }
            }
            return response;
        });
    }


    const onCloseFieldForm = () => {
        setAction(null);
        setSelectedField(null);
        setParentEntity(null);
    }

    const handleRowMove = (source, destination) => {

        if (source?.data?.parentId !== destination.data?.parentId) { // Only allow movement within the same parent
            return;
        }

        const previousData = data;

        const updateNestedData = (widget, sourceIndex, destIndex, movedFieldParentId) => {
            if (!widget.children || widget.children.length === 0) return widget;

            // Case 1: Moving within the top level children of selectedWidget
            if (movedFieldParentId === widget.id) {
                const updatedChildren = [...widget.children];
                const [movedItem] = updatedChildren.splice(sourceIndex, 1); // Remove item from old position
                updatedChildren.splice(destIndex, 0, movedItem); // Insert at new position

                return { ...widget, children: updatedChildren };
            }
            // Case 2: Moving inside nested children
            const reorderChildren = (items) => {
                return items.map(item => {
                    if (item.id === movedFieldParentId) {
                        if (!item.children || item.children.length === 0) return item;

                        const updatedChildren = [...item.children];
                        const [movedItem] = updatedChildren.splice(sourceIndex, 1);
                        updatedChildren.splice(destIndex, 0, movedItem);

                        return { ...item, children: updatedChildren };
                    }

                    if (item.children && item.children.length > 0) {
                        return { ...item, children: reorderChildren(item.children) };
                    }

                    return item;
                });
            };

            return { ...widget, children: reorderChildren(widget.children) };
        };

        const sourceIndex = selectedWidget.children.findIndex(item => item.id === source.data.id);
        const destinationIndex = selectedWidget.children.findIndex(item => item.id === destination.data.id);
        const updatedWidget = updateNestedData(
            selectedWidget,
            sourceIndex,
            destinationIndex,
            source.data.parentId
        );
        setData(generateTableData(updatedWidget.children ?? [], updatedWidget.id));
        const updatedView = updateViewSchema(selectedView, 'edit', { id: updatedWidget.id, updatedData: updatedWidget });
        dispatch(setDashboardRefresh({ type: "widget", action: "edit", entity: {id: selectedWidget.id, ...updatedWidget} }));
        handleViewUpdate(updatedView).then((response) => {
            if (response.status === false && response.code !== 255) {
                setData(previousData);
                dispatch(handleSnackBar({ open: true, message: "Something went wrong", severity: 'error' }));
            }
        }).catch(() => {
            setData(previousData);
            dispatch(handleSnackBar({ open: true, message: "Something went wrong", severity: 'error' }));
        });
    };

    const handleHyperlinkConfigOnclose = () =>{
        setShowHyperlinkConfiguration(false);
        setPopupAnchorEl(null);
    }

    return (
        <>
            {!action &&
                <>
                    <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title={'Create field'}>
                            <Button id="create_field_btn" color="primary" variant="contained" onClick={onCreateField} style={{ marginLeft: 10, marginBottom: 10 }}>
                                Create field
                            </Button>
                        </Tooltip>
                    </Grid>
                    <TanStackTable
                        columns={columns}
                        data={data}
                        tableId={"fields_table"}
                        enableRowMove={true}
                        onRowMove={handleRowMove}
                    />
                </>}
            {showHyperlinkConfiguration && 
            <HyperlinkConfiguration 
                anchorEl={popupAnchorEl} 
                anchorPos={anchorPosition}  
                open={showHyperlinkConfiguration} 
                setOpen={setShowHyperlinkConfiguration}
                onClose={handleHyperlinkConfigOnclose}  
                selectedView={selectedView}
                selectedField={selectedField}
                parentEntity={parentEntity} 
                widgetId={selectedWidget.id}
            /> }
            {action === "create_field" && <FieldForm selectedView={selectedView} widgetId={selectedWidget.id} action={"create"} parentEntity={parentEntity} onClose={() => setAction(null)} />}
            {action === "edit_field" && <FieldForm selectedView={selectedView} widgetId={selectedWidget.id} action={"edit"} parentEntity={parentEntity} selectedField={selectedField} onClose={onCloseFieldForm} />}

        </>
    )
}